import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/Privacy.css';

const Privacy = () => {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <div className="privacy-container">
      <nav className="privacy-nav">
        <div className="nav-content">
          <div className="logo" onClick={handleLogoClick}>
            Equation Live
          </div>
          <div className="nav-buttons">
            <button className="nav-button primary" onClick={() => navigate('/signin')}>
              Sign In
            </button>
          </div>
        </div>
      </nav>

      <main className="privacy-content">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p>Last updated: June 22nd, 2025</p>
        </div>

        <div className="privacy-body">
          <section className="policy-section">
            <p>We at Equation Live (together with our affiliates, "Equation Live", "we", "our" or "us") respect your privacy and are strongly committed to keeping secure any information we obtain from you or about you. This Privacy Policy describes our practices with respect to Personal Data that we collect from or about you when you use our website, applications, and services (collectively, "Services").</p>
            
            <p>This Privacy Policy does not apply to content that we process on behalf of customers of our business offerings, such as our API. Our use of that data is governed by our customer agreements covering access to and use of those offerings.</p>
          </section>

          <section className="policy-section">
            <h2>1. Personal Data We Collect</h2>
            <p>We collect personal data relating to you ("Personal Data") as follows:</p>
            
            <h3>Personal Data You Provide</h3>
            <p>We collect Personal Data if you create an account to use our Services or communicate with us as follows:</p>
            
            <h4>Account Information</h4>
            <p>When you create an account with us, we will collect information associated with your account, including your name, contact information, account credentials, payment information, and transaction history, (collectively, "Account Information").</p>
            
            <h4>User Content</h4>
            <p>We collect Personal Data that you provide in the input to our Services ("Content"), including your LaTeX equations, mathematical expressions, and other content you upload, such as files and images, depending on the features you use.</p>
            
            <h4>Communication Information</h4>
            <p>If you communicate with us, such as via email or our support channels, we may collect Personal Data like your name, contact information, and the contents of the messages you send ("Communication Information").</p>
            
            <h4>Other Information You Provide</h4>
            <p>We collect other information that you may provide to us, such as when you participate in our events or surveys or provide us with information to establish your identity or age (collectively, "Other Information You Provide").</p>

            <h3>Personal Data We Receive from Your Use of the Services</h3>
            <p>When you visit, use, or interact with the Services, we receive the following information about your visit, use, or interactions ("Technical Information"):</p>
            
            <h4>Log Data</h4>
            <p>We collect information that your browser or device automatically sends when you use our Services. Log data includes your Internet Protocol address, browser type and settings, the date and time of your request, and how you interact with our Services.</p>
            
            <h4>Usage Data</h4>
            <p>We collect information about your use of the Services, such as the types of content that you view or engage with, the features you use and the actions you take, as well as your time zone, country, the dates and times of access, user agent and version, type of computer or mobile device, and your computer connection.</p>
            
            <h4>Device Information</h4>
            <p>We collect information about the device you use to access the Services, such as the name of the device, operating system, device identifiers, and browser you are using. Information collected may depend on the type of device you use and its settings.</p>
            
            <h4>Location Information</h4>
            <p>We may determine the general area from which your device accesses our Services based on information like its IP address for security reasons and to make your product experience better, for example to protect your account by detecting unusual login activity.</p>
            
            <h4>Cookies and Similar Technologies</h4>
            <p>We use cookies and similar technologies to operate and administer our Services, and improve your experience. If you use our Services without creating an account, we may store some of the information described in this policy with cookies, for example to help maintain your preferences across browsing sessions.</p>

            <h3>Information We Receive from Other Sources</h3>
            <p>We receive information from our trusted partners, such as security partners, to protect against fraud, abuse, and other security threats to our Services, and from marketing vendors who provide us with information about potential customers of our business services.</p>
          </section>

          <section className="policy-section">
            <h2>2. How We Use Personal Data</h2>
            <p>We may use Personal Data for the following purposes:</p>
            <ul>
              <li>To provide, analyze, and maintain our Services, for example to render your LaTeX equations and mathematical expressions;</li>
              <li>To improve and develop our Services and conduct research, for example to develop new product features;</li>
              <li>To communicate with you, including to send you information about our Services and events, for example about changes or improvements to the Services;</li>
              <li>To prevent fraud, illegal activity, or misuses of our Services, and to protect the security of our systems and Services;</li>
              <li>To comply with legal obligations and to protect the rights, privacy, safety, or property of our users, Equation Live, or third parties.</li>
            </ul>
            
            <p>We may also aggregate or de-identify Personal Data so that it no longer identifies you and use this information for the purposes described above, such as to analyze the way our Services are being used, to improve and add features to them, and to conduct research. We will maintain and use de-identified information in de-identified form and not attempt to reidentify the information, unless required by law.</p>
          </section>

          <section className="policy-section">
            <h2>3. Disclosure of Personal Data</h2>
            <p>We may disclose your Personal Data in the following circumstances:</p>
            
            <h3>Vendors and Service Providers</h3>
            <p>To assist us in meeting business operations needs and to perform certain services and functions, we may disclose Personal Data to vendors and service providers, including providers of hosting services, customer service vendors, cloud services, content delivery services, support and safety monitoring services, email communication software, web analytics services, payment and transaction processors, and other information technology providers. Pursuant to our instructions, these parties will access, process, or store Personal Data only in the course of performing their duties to us.</p>
            
            <h3>Business Transfers</h3>
            <p>If we are involved in strategic transactions, reorganization, bankruptcy, receivership, or transition of service to another provider (collectively, a "Transaction"), your Personal Data may be disclosed in the diligence process with counterparties and others assisting with the Transaction and transferred to a successor or affiliate as part of that Transaction along with other assets.</p>
            
            <h3>Government Authorities or Other Third Parties</h3>
            <p>We may share your Personal Data, including information about your interaction with our Services, with government authorities, industry peers, or other third parties in compliance with the law (i) if required to do so to comply with a legal obligation, or in the good faith belief that such action is necessary to comply with a legal obligation, (ii) to protect and defend our rights or property, (iii) if we determine, in our sole discretion, that there is a violation of our terms, policies, or the law; (iv) to detect or prevent fraud or other illegal activity; (v) to protect the safety, security, and integrity of our products, employees, users, or the public, or (vi) to protect against legal liability.</p>
            
            <h3>Affiliates</h3>
            <p>We may disclose Personal Data to our affiliates, meaning an entity that controls, is controlled by, or is under common control with Equation Live. Our affiliates may use this Personal Data in a manner consistent with this Privacy Policy.</p>
          </section>

          <section className="policy-section">
            <h2>4. Retention</h2>
            <p>We'll retain your Personal Data for only as long as we need in order to provide our Services to you, or for other legitimate business purposes such as resolving disputes, safety and security reasons, or complying with our legal obligations. How long we retain Personal Data will depend on a number of factors, such as:</p>
            <ul>
              <li>Our purpose for processing the data (such as whether we need to retain the data to provide our Services);</li>
              <li>The amount, nature, and sensitivity of the information;</li>
              <li>The potential risk of harm from unauthorized use or disclosure;</li>
              <li>Any legal requirements that we are subject to.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>5. Your Rights</h2>
            <p>Depending on where you live, you may have certain statutory rights in relation to your Personal Data. For example, you may have the right to:</p>
            <ul>
              <li>Access your Personal Data and information relating to how it is processed.</li>
              <li>Delete your Personal Data from our records.</li>
              <li>Update or correct your Personal Data.</li>
              <li>Transfer your Personal Data to a third party (right to data portability).</li>
              <li>Restrict how we process your Personal Data.</li>
              <li>Withdraw your consent—where we rely on consent as the legal basis for processing at any time.</li>
              <li>Object to how we process your Personal Data.</li>
              <li>Lodge a complaint with your local data protection authority.</li>
            </ul>
            
            <p>You can exercise some of these rights through your Equation Live account. If you are unable to exercise your rights through your account, please submit your request through our support channels or to support@equationlive.com.</p>
          </section>

          <section className="policy-section">
            <h2>6. Children</h2>
            <p>Our Services are not directed to, or intended for, children under 13. We do not knowingly collect Personal Data from children under 13. If you have reason to believe that a child under 13 has provided Personal Data to Equation Live through the Services, please email us at support@equationlive.com. We will investigate any notification and, if appropriate, delete the Personal Data from our systems. Users under 18 must have permission from their parent or guardian to use our Services.</p>
          </section>

          <section className="policy-section">
            <h2>7. Security</h2>
            <p>We implement commercially reasonable technical, administrative, and organizational measures designed to protect Personal Data from loss, misuse, and unauthorized access, disclosure, alteration, or destruction. However, no Internet or email transmission is ever fully secure or error free. Therefore, you should take special care in deciding what information you provide to the Services. In addition, we are not responsible for circumvention of any privacy settings or security measures contained on the Service, or third-party websites.</p>
          </section>

          <section className="policy-section">
            <h2>8. Additional U.S. State Disclosures</h2>
            <p>Some U.S. state privacy laws require specific disclosures. The following table provides additional information about the categories of Personal Data we collect and how we use and disclose that information.</p>
            
            <h3>Category of Personal Data</h3>
            <p>We collect the following information, as described above:</p>
            <ul>
              <li>Identifiers, such as your name, contact details, IP address, and other device identifiers</li>
              <li>Commercial information, such as your transaction history</li>
              <li>Network activity information, such as Content and how you interact with our Services</li>
              <li>Communication information, such as your contact information when you send us email</li>
              <li>Geolocation data, such as the general area from which your device accesses our Services based on information like its IP address</li>
              <li>Your account credentials and payment information</li>
            </ul>

            <h3>Use of Personal Data</h3>
            <p>We use this information for the following purposes, as described above:</p>
            <ul>
              <li>Provide, analyze, and maintain our Services</li>
              <li>Improve and develop our Services and conduct research</li>
              <li>Communicate with you, including to send you information about our Services and events</li>
              <li>Prevent fraud, illegal activity, or misuses of our Services, and to protect the security of our systems and Services</li>
              <li>Comply with legal obligations and protect the rights, privacy, safety, or property of our users, Equation Live, or third parties</li>
            </ul>

            <h3>Disclosure of Personal Data</h3>
            <p>We may disclose this information in the following circumstances, as described above:</p>
            <ul>
              <li>Vendors, service providers, and affiliates to process in accordance with our instructions</li>
              <li>Government authorities or other third parties for the legal reasons described above</li>
              <li>Parties involved in Transactions</li>
            </ul>

            <p>We do not "sell" Personal Data or "share" Personal Data for cross-contextual behavioral advertising, and we do not process Personal Data for "targeted advertising" purposes (as those terms are defined under state privacy laws). We also do not process sensitive Personal Data for the purposes of inferring characteristics about a consumer.</p>
          </section>

          <section className="policy-section">
            <h2>9. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. When we do, we will publish an updated version and effective date on this page, unless another type of notice is required by applicable law.</p>
          </section>

          <section className="policy-section">
            <h2>10. Third-Party Privacy Policies</h2>
            <p>Our Services may integrate with or link to third-party services. We encourage you to review the privacy policies of these third-party services to understand how they collect, use, and share your information:</p>
            
            <h3>ChatGPT</h3>
            <p>Our extension integrates with ChatGPT. You can review ChatGPT's privacy policy at: <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer">https://openai.com/privacy</a></p>
            
            <h3>Google Chrome</h3>
            <p>Our extension operates within the Google Chrome browser. You can review Google Chrome's privacy policy at: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></p>
            
            <p>Please note that we are not responsible for the privacy practices of these third-party services. Your use of these services is subject to their respective privacy policies and terms of service.</p>
          </section>

          <section className="policy-section">
            <h2>11. How to Contact Us</h2>
            <p>Please contact support if you have any questions or concerns not already addressed in this Privacy Policy.</p>
            <div className="contact-info">
              <p><strong>Email:</strong> support@equationlive.com</p>
              <p><strong>Support:</strong> <a href="/support">Contact Support</a></p>
            </div>
          </section>
        </div>
      </main>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Equation Live</h4>
            <p>Making LaTeX editing and rendering easy in ChatGPT</p>
          </div>
          <div className="footer-section">
            <ul>
              <li><a href="/support">Support</a></li>
              <li><a href="/about">About</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Equation Live. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy; 